// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';
import { type FormProps, formValueSelector, reduxForm } from 'redux-form';

import DialogPanel from 'view/App/DialogPanels/DialogPanel';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import { hideDialogPanel } from 'state/commands/dialogPanelCommands';
import {
  getActivePanelId,
  getAdminSession,
  getIdentitySession,
  getIsServerPlatformWindows,
  getServerFolders,
  getServerStatus,
} from 'state/selectors';
import localizedStrings from 'util/localizedStrings';
import { ServerFolders } from 'portfolio-shared/view/Utilities/BrowseServerFolders';
import {
  BROWSE_SERVER_FOLDERS_PANEL_ID,
  SERVER_STATE_STOPPED,
} from 'util/constants';
import { getPathSeparator } from 'portfolio-shared/util/pathUtils';
import { FILETYPE, getFolderForPath } from 'portfolio-shared/util/folderUtils';
import type { Dispatch, ServerFolder, ServerStatus } from 'state/types';
import { actions as serverActions } from 'state/reducers/serverReducer';
import './BrowseServerFoldersDialog.css';
import { appendToPath } from 'portfolio-shared/util/pathUtils';

const {
  getServerFolders: getServerFoldersAction,
  updateServerFolders,
} = serverActions;

type Props = {
  activePanelId: ?string,
  adminSessionId: ?string,
  fileFilter: ServerFolder => boolean,
  getServerFoldersAction: typeof getServerFoldersAction,
  hideDialogPanel: typeof hideDialogPanel,
  identitySessionId: ?string,
  intl: IntlShape,
  onPathSelected: string => void,
  pathSeparator: string,
  selectedPath: ?string,
  serverFolders: ServerFolder[],
  serverStatus: ServerStatus,
  showFiles: boolean,
  updateServerFolders: typeof updateServerFolders,
} & FormProps;

type State = {
  refreshing: boolean,
};

class BrowseServerFoldersDialog extends Component<Props, State> {
  getSessionId: Function;
  isOkEnabled: Function;
  handleFolderSelected: Function;
  handleKeyDown: Function;
  handleToggleCollapse: Function;
  refreshFolders: Function;
  refreshAnimationDidEnd: Function;
  renderPanelHeader: Function;
  serverFoldersHandleKeyDown: ?Function;
  submit: Function;

  state = { refreshing: false };

  static defaultProps = {
    showFiles: false,
  };

  constructor() {
    super();
    this.getSessionId = this.getSessionId.bind(this);
    this.isOkEnabled = this.isOkEnabled.bind(this);
    this.handleFolderSelected = this.handleFolderSelected.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleToggleCollapse = this.handleToggleCollapse.bind(this);
    this.refreshFolders = this.refreshFolders.bind(this);
    this.refreshAnimationDidEnd = this.refreshAnimationDidEnd.bind(this);
    this.renderPanelHeader = this.renderPanelHeader.bind(this);
    this.submit = this.submit.bind(this);
  }

  componentDidMount() {
    const { getServerFoldersAction, showFiles } = this.props;
    const sessionId = this.getSessionId();
    if (sessionId) {
      getServerFoldersAction(sessionId, undefined, undefined, showFiles);
    }
  }

  // return either admin or identity session id, depending on server state and user
  getSessionId() {
    const { adminSessionId, identitySessionId, serverStatus } = this.props;
    const isStopped =
      serverStatus && serverStatus.runState === SERVER_STATE_STOPPED;
    const session =
      isStopped && adminSessionId ? adminSessionId : identitySessionId;
    return session;
  }

  refreshAnimationDidEnd() {
    this.setState({ refreshing: false });
  }

  submit(data) {
    this.props.onPathSelected(data.selectedPath);
  }

  refreshFolders() {
    const { getServerFoldersAction, showFiles } = this.props;
    const sessionId = this.getSessionId();
    if (sessionId) {
      getServerFoldersAction(sessionId, '', true, showFiles);
    }

    this.setState({ refreshing: true });
  }

  handleToggleCollapse(path) {
    const { pathSeparator, serverFolders, showFiles } = this.props;

    // Should this be moved into BrowseServerFolders?
    const folder = getFolderForPath(serverFolders, path, pathSeparator);
    folder.isExpanded = !folder.isExpanded;
    this.props.updateServerFolders(serverFolders);

    const sessionId = this.getSessionId();
    if (folder.isExpanded && sessionId) {
      this.props.getServerFoldersAction(sessionId, path, undefined, showFiles);
    }

    this.forceUpdate(); // This is what happens when you mutate state
  }

  handleFolderSelected(selectedPath) {
    const { pathSeparator, serverFolderRoot } = this.props;
    const absolutePath = appendToPath(
      serverFolderRoot,
      selectedPath,
      pathSeparator
    );

    this.props.change('selectedPath', absolutePath);
  }

  isOkEnabled() {
    return this.props.valid;
  }

  handleKeyDown(e: SyntheticKeyboardEvent<>) {
    this.serverFoldersHandleKeyDown && this.serverFoldersHandleKeyDown(e);
  }

  renderPanelHeader() {
    return (
      <header className="dialog-panel-header" style={{ display: 'flex' }}>
        <div style={{ width: '36px' }} />
        <div className="dialog-panel-title">
          <FormattedMessage {...localizedStrings.BROWSE_FOLDERS_TITLE} />
        </div>
        <div className="control">
          <a
            className="button is-primary is-outlined"
            data-test-name="refresh"
            onClick={this.refreshFolders}
            title={this.props.intl.formatMessage(localizedStrings.refresh)}
          >
            <span className="icon is-large">
              <i
                className={`fa fa-lg fa-refresh ${
                  this.state.refreshing ? 'fa-spin-once' : ''
                }`}
                onAnimationEnd={this.refreshAnimationDidEnd}
              />
            </span>
          </a>
        </div>
      </header>
    );
  }

  render() {
    const {
      fileFilter,
      handleSubmit,
      intl: { formatMessage },
      pathSeparator,
      selectedPath,
      serverFolders,
    } = this.props;

    return (
      <DialogPanel
        panelId={BROWSE_SERVER_FOLDERS_PANEL_ID}
        activePanelId={this.props.activePanelId}
        contentStyle={{ width: 520, height: 620 }}
        customButtonText={formatMessage(localizedStrings.SELECT)}
        handleKeyDown={this.handleKeyDown}
        hideDialogPanel={this.props.hideDialogPanel}
        isOkEnabled={this.isOkEnabled}
        onSave={handleSubmit(this.submit)}
        showCancel
        dataTestName="browse-server-folders-dialog"
        panelHeader={this.renderPanelHeader()}
      >
        <div className="portfolio-form-container">
          <ServerFolders
            onFolderSelected={this.handleFolderSelected}
            onToggleCollapse={this.handleToggleCollapse}
            outerDivStyle="browseServerFoldersContainer"
            pathSeparator={pathSeparator}
            selectedFolderPath={selectedPath}
            treeArray={serverFolders}
            fileFilter={fileFilter}
            ref={c => {
              this.serverFoldersHandleKeyDown = c ? c.handleKeyDown : undefined;
            }}
          />

          <div data-test-name="selected-path" className="selected-path">
            {selectedPath}
          </div>
        </div>
      </DialogPanel>
    );
  }
}

function validate(values: Object, props: Props) {
  const errors = {};

  if (!values.selectedPath) {
    errors.selectedPath = 'Required';
  } else if (
    // If we're looking at files, assume we only want files to be valid selections
    props.showFiles &&
    typeof props.fileFilter === 'function' &&
    !props.fileFilter({
      displayName: values.selectedPath,
      fullPath: values.selectedPath,
      isExpanded: false,
      type: FILETYPE.file,
    })
  ) {
    errors.selectedPath = 'Invalid Selection';
  }

  return errors;
}

const form = reduxForm({
  form: BROWSE_SERVER_FOLDERS_PANEL_ID,
  validate,
})(BrowseServerFoldersDialog);

const selector = formValueSelector(BROWSE_SERVER_FOLDERS_PANEL_ID);

export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
    adminSessionId: getAdminSession(state),
    identitySessionId: getIdentitySession(state),
    pathSeparator: getPathSeparator(getIsServerPlatformWindows(state)),
    selectedPath: selector(state, 'selectedPath'),
    // Deep copy of server folders so that we don't directly modify state
    // when toggling folders opened and closed.
    serverFolders: JSON.parse(JSON.stringify(getServerFolders(state))),
    serverStatus: getServerStatus(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      getServerFoldersAction,
      hideDialogPanel,
      updateServerFolders,
    })
)(injectIntl(form));



// WEBPACK FOOTER //
// ./src/view/App/DialogPanels/BrowseServerFoldersDialog.js